import { OrderSplitStatus, OtcProductStatus, RxFillRequestStatus } from '@truepill/tpos-types'
import { isSplitCancelled } from 'utils/splitOrder'

type RejectionCode = { reasonCode?: string | null } | null

// The original order as the split progress subscription selects it
export type SplitProgressOrder = {
  _id: string
  coreOrderToken?: string | null
  rxFillRequests?: {
    _id: string
    status: string
    rejectionDetails?: RejectionCode
    prescription?: { rxNumber?: string | null } | null
  }[]
  otcProducts?: { _id: string; name?: string | null; otcStatus: string; rejectionDetails?: RejectionCode }[] | null
  splits?:
    | {
        _id: string
        coreFillRequestToken: string
        status: OrderSplitStatus
        orderId?: string | null
        rxFillRequestIds: string[]
        otcProductIds: string[]
        splitOutOrder?: { _id: string; locationId: string; coreOrderToken?: string | null } | null
      }[]
    | null
}

export type SplitOutOrderRef = { orderId: string; coreOrderToken?: string | null; locationId?: string }

export type SplitProgress =
  | { kind: 'wait' }
  | { kind: 'drop' }
  // stayedLabels: selected items still live on the original
  | { kind: 'complete'; splitOutOrder: SplitOutOrderRef; movedCount: number; stayedLabels: string[] }
  // Intake created the split-out order but moved nothing: every selected item is live on both orders
  | { kind: 'gaveUp'; splitOutOrder: SplitOutOrderRef }

export const splitProgressFor = (order: SplitProgressOrder, coreFillRequestToken: string): SplitProgress => {
  const splitEntry = order.splits?.find(entry => entry.coreFillRequestToken === coreFillRequestToken)
  if (splitEntry?.status === OrderSplitStatus.Failed) return { kind: 'drop' }
  // Split entries are never removed, so an order without it is an update written before it: keep waiting
  if (!splitEntry?.orderId) return { kind: 'wait' }

  const splitOutOrder = {
    orderId: splitEntry.orderId,
    coreOrderToken: splitEntry.splitOutOrder?.coreOrderToken,
    locationId: splitEntry.splitOutOrder?.locationId,
  }
  if (splitEntry.status !== OrderSplitStatus.Complete) return { kind: 'gaveUp', splitOutOrder }

  const fills = (order.rxFillRequests ?? []).filter(fill => splitEntry.rxFillRequestIds.includes(fill._id))
  const otcProducts = (order.otcProducts ?? []).filter(otc => splitEntry.otcProductIds.includes(otc._id))
  const movedCount = [...fills, ...otcProducts].filter(item => isSplitCancelled(item.rejectionDetails)).length
  const stayedLabels = [
    ...fills
      .filter(fill => fill.status !== RxFillRequestStatus.Cancelled)
      .map(fill => `RX ${fill.prescription?.rxNumber ?? ''}`.trim()),
    ...otcProducts.filter(otc => otc.otcStatus !== OtcProductStatus.Cancelled).map(otc => otc.name ?? 'OTC'),
  ]
  return { kind: 'complete', splitOutOrder, movedCount, stayedLabels }
}

const atLocation = (locationName?: string): string => (locationName ? ` at ${locationName}` : '')

export const splitCompleteMessage = (
  originalCoreOrderToken: SplitProgressOrder['coreOrderToken'],
  { splitOutOrder, movedCount, stayedLabels }: Extract<SplitProgress, { kind: 'complete' }>,
  locationName?: string,
): string => {
  const items = movedCount === 1 ? '1 item' : `${movedCount} items`
  const newOrder = `order ${splitOutOrder.coreOrderToken}${atLocation(locationName)}`
  const moved = `Order ${originalCoreOrderToken} split: ${items} moved to ${newOrder}.`
  if (!stayedLabels.length) return moved
  const stayed = stayedLabels.join(', ')
  return `${moved}\nStayed on order ${originalCoreOrderToken}: ${stayed}. The new order's matching lines are in Triage.`
}

export const splitGaveUpMessage = (
  originalCoreOrderToken: SplitProgressOrder['coreOrderToken'],
  { splitOutOrder }: Extract<SplitProgress, { kind: 'gaveUp' }>,
  locationName?: string,
): string => {
  const created = `order ${splitOutOrder.coreOrderToken} was created${atLocation(locationName)}`
  return (
    `Split of order ${originalCoreOrderToken} didn't finish: ${created} but no items moved. ` +
    'Both copies are live. Review the triage on the new order.'
  )
}
